import { particleCap } from '../graphics';
import {
  artOfSkill,
  ELEMENT_INFO,
  getNeigong,
  getSkill,
  TITLES,
  groundAt,
  neigongMastered,
  MOUNTS,
  mulberry32,
  SKILLS,
  type Entity,
  type SkillDef,
  type World,
  type WorldEvent,
} from '@nine/shared';
import type { Sfx } from '../audio';
import { oldFxOn } from './oldSkillFx';
import { settings } from '../settings';
import type { IsoCamera } from './camera';
import { SkillFxSet, type Fx, type FxHost, type Particle, type VisualSword } from './fx/registry';
import { dirOf, drawSheet, drawTell, type SheetSpec } from './fxSheets';
import { artH, artW, artX, artY } from './pixel';
import {
  fxWeaponOf,
  LEAP_LANDS,
  MIN_SWING,
  SKILL_SWINGS,
  skillLook,
  type CastStyle,
  type FxWeapon,
  type SkillLook,
  type SwingStyle,
} from './skillFx';
import { viewOf, visualScale, type SpriteBank } from './sprites';
import { clashFromEvent } from './fx/clash';
import { hitReactOnHit } from './hitReact';
import { guardReadOnHit } from './guardRead';
import { hitStopOnHit } from './hitStop';
import { drawColumn, fxPixelOn, fxStep, ghostOf, PART_FPS } from './fx/pixelRules';
import { lastShotOf } from './lastShot';
import { drawGhost } from './fx/afterimage';
import { dispatchFxEvent } from './fx/events/registry';

import {
  inkDragon,
  inkGeneric,
  inkGenericBlood,
  inkHandles,
  inkHitBlood,
  inkImpact,
  inkRelease,
  inkVolley,
  isInk,
} from './ink/game';

export type { CastStyle, SwingStyle } from './skillFx';

// Every skill's own effect (render/fx/<fx>.ts), each registering itself with the registry.
import.meta.glob(['./fx/*.ts', '!./fx/registry.ts'], { eager: true });
// Every world event's reaction (render/fx/events/<family>.ts), each registering itself by type.
import.meta.glob(['./fx/events/*.ts', '!./fx/events/registry.ts'], { eager: true });

export interface FloatText {
  x: number;
  y: number;
  text: string;
  color: string;
  size: number;
  t: number;
  life: number;
  /** Move-name callout: drawn large with this Han glyph in a seal. */
  glyph?: string;
}

/** Per-entity animation memory: things the simulation does not keep but the animation needs. */
export interface AnimState {
  combo: number;
  style: SwingStyle;
  swingDur: number;
  /** When the current swing began (it plays for `swingDur`, at least its style's `MIN_SWING`). */
  swingAt: number;
  /** The swing came out of a charged cast, so it skips the anticipation frame. */
  charged: boolean;
  castStyle: CastStyle;
  /** World times of the last landing, blocked hit, air jump and death. */
  landAt: number;
  /** When the last swing ghost was left behind. */
  ghostAt: number;
  /** When the guard went up (−1 while it is down). */
  blockSince: number;
  /** When the weapon was last put away or drawn, and which (`true`: put away). */
  sheatheAt: number;
  sheatheOn: boolean;
  guardAt: number;
  flipAt: number;
  deathAt: number;
  /** Time until the next footstep puff. */
  stepT: number;
  /** When this fighter's blow last connected (a brief freeze on the strike frame). */
  hitstopAt: number;
  /** How long that freeze holds (s), set by a weapon clash (fx/clash.ts); unset: the renderer's default. */
  hitstopHold?: number;
  /** Qi aura flaring around the fighter after a move: when, its colour, and its strength (0–1.5). */
  auraAt: number;
  auraCol: string;
  auraPow: number;
  /** Ground height drawn under the fighter (eases toward the boulder top it stands on). */
  elev: number;
  /**
   * Where the fighter was, and how they looked, over the last moments of a qinggong run (newest
   * last): the running clones follow along it.
   */
  trail: { x: number; y: number; img: HTMLCanvasElement; flip: boolean; ax: number; ay: number }[];
  /** Warps made so far in the current warp chain (each strikes in the next pose of the set). */
  chain: number;
  /** Last swing frame drawn, to leave an afterimage when the next one replaces it. */
  lastSwing: HTMLCanvasElement | null;
  /**
   * Fabric lag as a damped spring, in sprite pixels (positive = trailing behind). Pulled back by
   * movement, it overshoots when the fighter stops and swings across when they turn.
   */
  cloth: number;
  clothV: number;
  /** Last screen position and facing, to measure motion between frames. */
  sx: number;
  sy: number;
  face: number;
}

/** Spring stiffness and damping for fabric lag (slightly underdamped, so robes swing past rest). */
const CLOTH_K = 70;
const CLOTH_DAMP = 5;

const R = Math.random;

/** Arc geometry per swing style, in facing-right space (y down). */
const ARCS: Partial<Record<SwingStyle, { a0: number; a1: number; rx: number; ry: number }>> = {
  cleave: { a0: -2.0, a1: 1.0, rx: 21, ry: 15 },
  rising: { a0: 1.1, a1: -1.7, rx: 20, ry: 16 },
  overhead: { a0: -2.3, a1: 0.9, rx: 24, ry: 19 },
  spin: { a0: 0, a1: Math.PI * 2, rx: 30, ry: 12 },
};

/** Turns world events into short-lived visuals and sounds. Owns nothing the simulation depends on. */
export class Effects implements FxHost {
  fx: Fx[] = [];
  parts: Particle[] = [];
  texts: FloatText[] = [];
  /** Particle time not yet stepped (FX-PIXEL). */
  private partAcc = 0;
  /** Speech bubbles over heads (townsfolk barks), by entity id: the line and its age in seconds. */
  readonly bubbles = new Map<number, { text: string; t: number }>();
  static readonly BUBBLE_LIFE = 3.8;
  swords: VisualSword[] = [];
  shake = 0;
  /** Full-screen colour flash (rgb) and its strength 0–1, fading each frame. */
  flash = { col: '255,255,255', a: 0 };
  /** Brief camera zoom kick on heavy blows, 0–1. */
  punch = 0;
  /**
   * Cinematic moment of a big move: the world dims around the caster (who stays lit) and letterbox
   * bars slide in. Strength is `cineMax`, held while `cineT` runs and easing out over its last 0.3 s.
   */
  cineT = 0;
  cineMax = 0;
  cineSrc = -1;
  /** The camera closes in on the caster during the moment (ultimates only). */
  cineZoom = false;
  /** The move's glyph stamped across the screen in an ink splash (power moves). */
  stamp: { glyph: string; name: string; col: string; t: number; life: number; seed: number } | null = null;
  private ghostT = 0;
  private foeGhostT = 0;
  /** Split-body afterimages left so far in the current fast move (3 per move). */
  private ghostN = 0;
  private foeGhostN = 0;
  private sprintGhostT = 0;
  private rideGhostT = 0;
  private auraT = 0;
  private auraBigT = 0;
  private auraPulseT = 0;
  private skinT = 0;
  private dripT = 0;
  private hakiT = 0;
  private pkT = 0;
  private rideSparkT = 0;
  /** Recent points of the flying sword's tail (world pixels, age in seconds): its light trail. */
  private swordTrail: { x: number; y: number; t: number }[] = [];
  private readonly anims = new Map<number, AnimState>();
  /** Frost creeping in round the screen (the last cut of เหมันต์สะกดธาร), 0–1, fading. */
  frost = 0;
  /** The trail of the move being set up, when not its weapon's (see `SkillLook.trail`). */
  private trail: FxWeapon | null = null;
  /** Each skill's own effect (render/fx/*.ts). */
  readonly skillFx: SkillFxSet;

  constructor(
    readonly world: World,
    readonly iso: IsoCamera,
    readonly sprites: SpriteBank,
    readonly sfx: Sfx,
    readonly onPlayerHurt: () => void,
  ) {
    this.skillFx = new SkillFxSet(this);
  }

  /** Drops effects pinned to screen positions (they would be misplaced after the camera turns). */
  clearPositioned(): void {
    this.fx = [];
    this.parts = [];
    this.texts = [];
    this.swords = [];
    this.swordTrail = [];
    // Screen positions all jump when the view turns; that is not movement the cloth should feel.
    for (const a of this.anims.values()) {
      a.sx = Number.NaN;
      a.trail.length = 0;
    }
  }

  /** RGB of the element a move belongs to (enemy moves glow blood red). */
  elemColor(skillId: string): string {
    const art = artOfSkill(skillId);
    if (!art) return '255,90,90';
    const hex = ELEMENT_INFO[art.element].color;
    return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(',');
  }

  /** Current strength of the cinematic dim and letterbox, 0–1. */
  get cine(): number {
    return settings.cinematic ? this.cineMax * Math.min(1, this.cineT / 0.3) : 0;
  }

  /** Starts (or strengthens) a cinematic moment centred on a fighter. */
  cinematic(src: Entity, strength: number, hold: number, zoom = false): void {
    if (this.cine > strength && this.cineSrc !== src.id) return;
    if (this.cineSrc !== src.id || this.cine < 0.01) this.cineZoom = false;
    this.cineMax = Math.max(strength, this.cine);
    this.cineT = Math.max(this.cineT, hold);
    this.cineSrc = src.id;
    this.cineZoom ||= zoom;
  }

  /** Flash the screen and kick the camera; stronger calls win. */
  impact(col: string, flash: number, punch: number, shake: number): void {
    if (flash > this.flash.a) this.flash = { col, a: flash };
    this.punch = Math.max(this.punch, punch);
    this.shake = Math.max(this.shake, shake);
  }

  /** The move's name over the caster, wuxia style, with its glyph in a seal. */
  private callout(src: Entity, s: { name: string; glyph: string }, col: string): void {
    if (!settings.callouts) return;
    const [x, y] = this.pos(src);
    const set = this.sprites.of(src);
    const h = (set?.h ?? 40) * visualScale(src, set);
    // Earlier callouts step up so consecutive moves stack instead of overlapping.
    for (const t of this.texts) if (t.glyph) t.y -= 12;
    this.texts.push({
      x,
      // High above the name tags, so the fighter and the move itself stay in view.
      y: y - h - 34,
      text: s.name,
      color: `rgb(${col})`,
      size: 22,
      t: 0,
      life: 1.3,
      glyph: s.glyph,
    });
  }

  /**
   * How high a mount carries its rider above the ground (world pixels), bobbing gently. Zero on
   * foot. Purely visual: the simulation keeps the rider on the ground.
   */
  hoverOf(e: Entity): number {
    const m = e.mount ? MOUNTS[e.mount] : undefined;
    return m ? m.hover + Math.sin(this.world.time * 3 + e.id) : 0;
  }

  /** Animation memory for an entity (created on first use). */
  anim(id: number): AnimState {
    let a = this.anims.get(id);
    if (!a) {
      a = {
        combo: 0,
        style: 'cleave',
        swingDur: 0.22,
        swingAt: -9,
        charged: false,
        castStyle: 'cast',
        landAt: -9,
        sheatheAt: -9,
        blockSince: -1,
        ghostAt: -9,
        sheatheOn: false,
        guardAt: -9,
        flipAt: -9,
        deathAt: -9,
        stepT: 0,
        hitstopAt: -9,
        auraAt: -9,
        auraCol: '255,255,255',
        auraPow: 0,
        chain: 0,
        trail: [],
        elev: 0,
        lastSwing: null,
        cloth: 0,
        clothV: 0,
        sx: Number.NaN,
        sy: 0,
        face: 1,
      };
      this.anims.set(id, a);
    }
    return a;
  }

  /**
   * Fabric state to draw for an entity: lag in whole pixels (halved when seen head-on, where it reads
   * as wind) and a flutter phase for hair and ribbons while the cloth is streaming.
   */
  clothOf(e: Entity, headOn: boolean): { sway: number; flutter: number } {
    const a = this.anim(e.id);
    const x = headOn ? a.cloth * 0.5 : a.cloth;
    const sway = Math.max(-2, Math.min(3, Math.round(x)));
    const flutter = Math.abs(a.cloth) > 1.3 ? Math.floor(this.world.time * 9) % 4 : 0;
    return { sway, flutter };
  }

  /** Steps every entity's fabric spring from how far it moved on screen this frame. */
  private stepCloth(dt: number): void {
    if (dt <= 0) return;
    for (const e of this.world.entities.values()) {
      if (!this.sprites.of(e)?.cloth) continue;
      const a = this.anim(e.id);
      const [x, y] = this.pos(e);
      const face = this.iso.facing(e);
      if (Number.isNaN(a.sx)) [a.sx, a.sy, a.face] = [x, y - e.z, face];
      const speed = Math.hypot(x - a.sx, y - e.z - a.sy) / dt;
      // A turn mirrors the sprite: cloth that trailed left now hangs on the leading side and swings back.
      if (face !== a.face) {
        a.cloth = -a.cloth;
        a.clothV = -a.clothV;
      }
      // Only the fabric's lag on top of the pose's own swing (a walk or run already streams its
      // hair and hems): enough to carry on swinging after a stop or a turn, not to blow a walk's
      // ponytail out flat.
      const target = Math.min(1, speed * 0.008);
      a.clothV += (CLOTH_K * (target - a.cloth) - CLOTH_DAMP * a.clothV) * dt;
      a.cloth += a.clothV * dt;
      [a.sx, a.sy, a.face] = [x, y - e.z, face];
    }
  }

  /**
   * How much taller this entity's figure is than the original 56 px design, for placing effects at
   * chest height (1 for beasts, whose numbers are tuned to their own size).
   */
  tall(e: Entity | undefined | null): number {
    const set = e && this.sprites.of(e);
    return set?.poses.cut ? set.h / 56 : 1;
  }

  /** Where the fighter stands on screen: on the ground, or up on the boulder underfoot. */
  /** C0-VFX-PORT: where a forward dash started (its `skillDash` comes just before its `skill`), screen px. */
  dashFrom = new Map<number, [number, number]>();

  pos(e: Entity): [number, number] {
    const [x, y] = this.iso.proj(e.x, e.y);
    return [x, y - this.anim(e.id).elev];
  }

  /** Eases the drawn ground height toward the ground under the fighter (a quick step up or down). */
  stepElevation(e: Entity): number {
    const a = this.anim(e.id);
    const target = groundAt(this.world.map, e.x, e.y);
    const d = target - a.elev;
    a.elev = Math.abs(d) < 0.6 ? target : a.elev + d * 0.35;
    return a.elev;
  }

  armed(e: Entity): boolean {
    return this.sprites.of(e)?.armed ?? false;
  }

  /** Strikes with a weapon (not bare hands, claws or fangs). */
  isArmed(e: Entity): boolean {
    const w = this.weaponOf(e);
    return w !== 'fist' && w !== 'claw';
  }

  burst(x: number, y: number, n: number, cols: string[], spd = 40, up = 30, life = 0.5): void {
    for (let k = 0; k < n; k++) {
      const a = R() * Math.PI * 2;
      const s = spd * (0.4 + R() * 0.6);
      this.parts.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s * 0.5 - up * R(),
        g: 80,
        t: 0,
        life: life * (0.6 + R() * 0.6),
        c: cols[k % cols.length]!,
      });
    }
  }

  /** Sparks flying away from the attacker along `dir` (±1), drawn as short streaks. */
  sparks(x: number, y: number, dir: number, n: number, cols: string[], spd = 120): void {
    for (let k = 0; k < n; k++) {
      const a = (R() - 0.5) * 1.6;
      const s = spd * (0.5 + R() * 0.7);
      this.parts.push({
        x,
        y,
        vx: Math.cos(a) * s * dir,
        vy: Math.sin(a) * s * 0.6 - 20,
        g: 160,
        t: 0,
        life: 0.18 + R() * 0.2,
        c: cols[k % cols.length]!,
        streak: true,
        drag: 4,
      });
    }
  }

  /** Dust kicked up at the feet. */
  dust(x: number, y: number, n: number, spread: number, spd = 25): void {
    for (let k = 0; k < n; k++) {
      const side = k % 2 ? 1 : -1;
      this.parts.push({
        x: x + side * R() * spread,
        y: y - R() * 2,
        vx: side * spd * (0.4 + R() * 0.6),
        vy: -8 - R() * 10,
        g: 20,
        t: 0,
        life: 0.35 + R() * 0.25,
        c: R() < 0.5 ? '#c8bca4' : '#9c9282',
        s: R() < 0.4 ? 2 : 1,
        drag: 3,
      });
    }
  }

  float(e: Entity, text: string, color: string, size: number): void {
    const [x, y] = this.pos(e);
    const set = this.sprites.of(e);
    const h = (set?.h ?? 40) * visualScale(e, set);
    this.texts.push({ x: x + (R() - 0.5) * 10, y: y - h - 2, text, color, size, t: 0, life: 1 });
  }

  /** The weapon family a fighter strikes with (see skillFx.ts). */
  weaponOf(e: Entity): FxWeapon {
    return fxWeaponOf(e, !!this.sprites.of(e)?.poses.cut);
  }

  /**
   * The swing's trail for a style, centred on the attacker's chest. A figure's is the shape of
   * its weapon's (a jian's keen crescent, a dao's broad heavy one, a staff's blunt streaks, a fan's
   * ribs, a spear's long twisting thrust, a palm's ghostly hand), drawn from cached frames.
   */
  swingArc(src: Entity, style: SwingStyle, col: string, scale = 1): void {
    const [x, y] = this.pos(src);
    const s = src.scale * scale;
    const f = this.iso.facing(src);
    if (this.sprites.of(src)?.poses.cut) {
      if (style === 'chest' || style === 'heal') return;
      const weapon = this.trail ?? this.weaponOf(src);
      const whole = style === 'spin' || style === 'fan';
      const kind: SheetSpec['kind'] =
        style === 'palm' || (weapon === 'fist' && (style === 'thrust' || style === 'qi'))
          ? 'palm'
          : style === 'thrust' || style === 'spear'
            ? 'thrust'
            : 'slash';
      this.fx.push({
        type: 'sheet',
        x: x + (whole ? 0 : f * 3 * s),
        y: y - (whole ? 10 : kind === 'palm' ? 20 : 17) * s * this.tall(src),
        t: 0,
        life: whole ? 0.36 : kind === 'thrust' ? 0.24 : 0.3,
        s: {
          kind,
          tint: col,
          size: s,
          flip: f < 0,
          weapon: style === 'spear' ? 'spear' : weapon,
          arc: style === 'fan' && weapon !== 'fan' ? 'spin' : style,
        },
      });
      return;
    }
    const geo = ARCS[style];
    if (geo)
      this.fx.push({
        type: 'arc',
        x: x + this.iso.facing(src) * 3 * s,
        y: y - (style === 'spin' ? 10 : 17) * s * this.tall(src),
        t: 0,
        life: style === 'spin' ? 0.34 : 0.28,
        f: this.iso.facing(src),
        a0: geo.a0,
        a1: geo.a1,
        rx: geo.rx * s,
        ry: geo.ry * s,
        w: (style === 'spin' ? 6 : 8) * s,
        col,
      });
    else if (style === 'thrust' || style === 'palm')
      this.fx.push({
        type: 'thrust',
        x,
        y: y - 18 * s * this.tall(src),
        t: 0,
        life: 0.22,
        f: this.iso.facing(src),
        len: (style === 'palm' ? 18 : 30) * s,
        col,
      });
  }

  /**
   * Records how the attacker's swing should be animated: from now, for the simulation's swing or
   * (standing still after it) the least time its style reads in.
   */
  startSwing(src: Entity, style: SwingStyle, charged: boolean): void {
    const a = this.anim(src.id);
    a.style = style;
    a.charged = charged;
    a.swingAt = this.world.time;
    a.swingDur = Math.max(0.12, src.swingT, MIN_SWING[style] ?? 0);
  }

  handle(ev: WorldEvent): void {
    const w = this.world;
    if (ev.type === 'pvpDuel') clashFromEvent(this, ev, (e) => this.isArmed(e));
    if (ev.type === 'hit') hitStopOnHit(ev, w.time);
    if (ev.type === 'hit') hitReactOnHit(ev, w);
    if (ev.type === 'hit') guardReadOnHit(ev, w);
    // Each event type's own reaction (render/fx/events/*.ts).
    dispatchFxEvent(this, ev, w);
  }

  /** Beasts have no swing frames: a quick bite arc at the muzzle instead. */
  bite(src: Entity): Fx {
    const [x, y] = this.pos(src);
    return {
      type: 'arc',
      x: x + this.iso.facing(src) * 10 * src.scale,
      y: y - 9 * src.scale,
      t: 0,
      life: 0.18,
      f: this.iso.facing(src),
      a0: -1.2,
      a1: 1.2,
      rx: 8 * src.scale,
      ry: 7 * src.scale,
      w: 3,
      col: '255,140,120',
    };
  }

  /** C0-VFX: a landed ink move's blood (or, with blood off, the clean flash): the sample's, else its template's. */
  inkBlood(src: Entity, skillId: string, sx: number, sy: number, x: number, y: number): void {
    const s = SKILLS[skillId];
    // The spectacle by weight (render/ink/spectacle.ts): a power blow heavy, a break moderate, others light.
    if (s) {
      const w = s.kind === 'power' ? 2 : s.kind === 'break' ? 1 : 0;
      inkImpact(this.fx, s.kind, w, sx, sy, x, y);
      const col = this.elemColor(skillId);
      if (w === 2) this.impact(col, 0.3, 0.9, 7);
      else if (w === 1) this.impact(col, 0.15, 0.5, 4);
    }
    if (isInk(skillId)) return inkHitBlood(this.fx, skillId, sx, sy, x, y, settings.blood);
    if (!s) return;
    const look = skillLook(s, this.weaponOf(src));
    inkGenericBlood(this.fx, s, look.category, this.anim(src.id).style, sx, sy, x, y, settings.blood);
  }

  /** Swing style for a skill's animation (skillFx.ts); a pair of styles alternates move by move. */
  private styleOf(s: SkillDef, src: Entity): SwingStyle {
    const m = skillLook(s, this.weaponOf(src)).motion;
    if (typeof m === 'string') return m;
    const a = this.anim(src.id);
    return m[a.combo++ % m.length]!;
  }

  onSkill(
    srcId: number,
    skillId: string,
    tgtId: number | null,
    volley?: { tgtId: number; at: number }[],
    spot?: { x: number; y: number },
  ): void {
    const w = this.world;
    const src = w.get(srcId);
    if (!src) return;
    const tgt = w.get(tgtId);
    const s = getSkill(skillId);
    const [sx, sy] = this.pos(src);
    // A ground-targeted move plays out at the spot picked; others at the target or the caster.
    const [tx, ty] = tgt ? this.pos(tgt) : spot ? this.iso.proj(spot.x, spot.y) : [sx, sy];
    const style = this.styleOf(s, src);
    this.startSwing(src, style, s.windup > 0);
    if (s.blink) this.anim(src.id).chain = 0;
    const humanoid = !!this.sprites.of(src)?.poses.cut;
    const brk = s.kind === 'break';
    // The spectacle layer every move gets on top of its own effect: grander for big moves.
    const col = this.elemColor(s.id);
    const grand = s.kind === 'power' || s.cooldown >= 8 || !!s.volley || !!s.aoe || !!s.telegraph;
    // A power move's name is stamped beside the scene instead (when cinematics are on).
    const stamped = s.kind === 'power' && src.kind === 'player' && settings.cinematic;
    if ((src.kind === 'player' || src.brain?.defId.endsWith('boss')) && !stamped) this.callout(src, s, col);
    // Old skill FX off (render/oldSkillFx.ts): the motion and the power move's cinematic only; a port draws its own.
    if (!oldFxOn()) {
      if ((src.kind === 'player' || src.brain?.defId.endsWith('boss')) && s.kind === 'power')
        this.cinematic(src, 1, 0.95, true);
      return;
    }
    // Qi aura: every move makes the fighter's inner energy flare; big moves burn brighter.
    const A = this.anim(src.id);
    A.auraAt = w.time;
    A.auraCol = col;
    A.auraPow = s.kind === 'power' ? 1.5 : grand ? 1 : 0.6;
    this.qiWisps(sx, sy, src, col, s.kind === 'power' ? 26 : grand ? 16 : 7);
    const hero = src.kind === 'player' || src.brain?.defId.endsWith('boss');
    // C0-VFX: an ink sample draws everything itself; only the cinematic moment of a power move stays.
    // Every other move (bar volleys and warp chains) gets its kind's ink template, lined up with
    // its motion's contact (render/ink/kinds.ts). No name stamp (the user: no seals).
    const inkLook = skillLook(s, this.weaponOf(src));
    const sw = SKILL_SWINGS[style];
    const contact = (sw ? sw.contact : 0.3) * this.anim(src.id).swingDur;
    const has = !!tgt || !!spot;
    // C0-VFX-PORT (QA VFX-R1 MF1/MF2): a move with no target or spot (a spin, a power round the caster) aims where the
    // caster is fighting — its own target when within reach, else 2 tiles ahead along its facing — through the turned
    // camera, so the effect lands on the foe and follows the yaw (it used to fall 64 px screen-right).
    const own = tgt ? null : w.get(src.targetId ?? null);
    const reach = Math.max(3, s.range, s.aoe ?? 0);
    const near = own && !own.dead && Math.hypot(own.x - src.x, own.y - src.y) <= reach ? own : null;
    const [ax, ay] = has
      ? [tx, ty]
      : near
        ? this.pos(near)
        : this.iso.proj(src.x + (src.dirX || 1) * 2, src.y + (src.dirY || 0) * 2);
    // A forward dash already carried the fighter: its effect runs from where it started toward where it ended.
    const df = s.dash === 'forward' ? this.dashFrom.get(src.id) : undefined;
    this.dashFrom.delete(src.id);
    const [csx, csy] = df ?? [sx, sy];
    const [cax, cay] = df ? [sx + (sx - df[0]) * 0.15, sy + (sy - df[1]) * 0.15] : [ax, ay];
    // C0-VFX: the golden dragon palm's dragons (render/ink/dragon.ts).
    if (
      inkDragon(
        this.fx,
        s,
        sx,
        sy,
        tx,
        ty,
        has,
        contact,
        volley?.map((v) => v.at),
      )
    ) {
      if (hero && s.kind === 'power') this.cinematic(src, 1, 0.95, true);
      return;
    }
    if (volley?.length) {
      // A volley's projectiles as brush darts, each to its target, landing when the world says.
      const shots = volley.flatMap((v) => {
        const e = w.get(v.tgtId);
        if (!e) return [];
        const [vx, vy] = this.pos(e);
        return [{ tx: vx, ty: vy, at: Math.max(0.05, v.at) }];
      });
      inkVolley(this.fx, s, sx, sy, shots);
    }
    const inked =
      !!volley?.length ||
      !!s.blink ||
      inkRelease(this.fx, s.id, csx, csy, cax, cay, true) ||
      (inkHandles(s) &&
        (inkGeneric(this.fx, s, inkLook.category, style, csx, csy, cax, cay, true, contact), true));
    if (inked) {
      if (hero && s.kind === 'power') this.cinematic(src, 1, 0.95, true);
      return;
    }
    if (hero && grand) {
      // The world holds its breath: dim everything but the caster, slide in the letterbox.
      this.cinematic(src, s.kind === 'power' ? 1 : 0.55, s.kind === 'power' ? 0.95 : 0.5, s.kind === 'power');
      if (s.kind === 'power' && settings.cinematic)
        this.stamp = { glyph: s.glyph, name: s.name, col, t: 0, life: 1.15, seed: R() * 999 };
      // A ring of qi gathering in to the caster, then rushing out.
      this.fx.push({ type: 'gather', x: sx, y: sy, t: 0, life: 0.35, r: 44, col });
      this.fx.push({ type: 'ring', x: sx, y: sy, t: 0, life: 0.5, r: 30, col: '255,255,255' });
    }
    const look = skillLook(s, this.weaponOf(src));
    // A move may trail something other than the weapon (a dancer's sleeves), for this move only.
    this.trail = look.trail ?? null;
    // A blade's cut reaching past its length flies on as sword qi, whatever the move.
    const flies =
      look.projectile ??
      (this.armed(src) &&
      !s.blink &&
      !s.dash &&
      !s.volley &&
      (look.category === 'slash' || look.category === 'thrust' || look.category === 'qi')
        ? 'qi'
        : undefined);
    if (hero && tgt && flies && Math.hypot(tgt.x - src.x, tgt.y - src.y) > 1.8) {
      // Sword qi (a crescent in the move's element), or a palm's force wave, flying to the foe.
      const h = 16 * this.tall(src);
      const y1 = ty - 14 * this.tall(tgt);
      this.fx.push({
        type: 'sheet',
        x: sx,
        y: sy - h,
        t: 0,
        life: flies === 'qi' ? 0.26 : 0.32,
        x1: tx,
        y1,
        s:
          flies === 'qi'
            ? { kind: 'qi', tint: look.element, size: grand ? 1.25 : 1, dir: dirOf(tx - sx, y1 - sy + h) }
            : { kind: 'palmWave', tint: look.element, size: grand ? 1.25 : 1, flip: tx < sx },
      });
    }
    if (look.slam) {
      // The ground slammed where the move comes down (after the leap, for a leaping strike).
      const [gx, gy] = spot ? [tx, ty] : s.aoe || !tgt ? [sx, sy] : [tx, ty];
      const leap = this.anim(src.id).style === 'leap';
      this.fx.push({
        type: 'sheet',
        x: gx,
        y: gy,
        t: leap ? -this.anim(src.id).swingDur * LEAP_LANDS : 0,
        life: 0.7,
        s: {
          kind: 'slam',
          tint: look.element,
          size: 1,
          r: Math.max(24, Math.round(((s.aoe ?? 1.3) * 24) / 12) * 12),
        },
      });
    }
    if (s.kind === 'heal' || s.kind === 'buff')
      this.fx.push({
        type: 'sheet',
        x: sx,
        y: sy,
        t: 0,
        life: s.kind === 'heal' ? 1 : 0.85,
        s: { kind: s.kind, tint: look.element, size: src.scale },
      });
    if (look.extra) this.extraMark(look.extra, src, tgt, s, spot ? [tx, ty] : null, look.element);
    if (src.kind === 'player' && s.kind !== 'heal' && s.kind !== 'buff') {
      const [ix, iy] = tgt ? [tx, ty] : [sx, sy];
      const ch = 14 * this.tall(tgt ?? src);
      this.fx.push({
        type: 'lines',
        x: ix,
        y: iy - ch,
        t: 0,
        life: 0.25,
        r: grand ? 46 : 28,
        col,
        seed: R() * 999,
      });
      if (grand) {
        // A warp chain's reach is how far it travels, not a blast radius.
        const area = s.blink ? undefined : s.aoe;
        this.fx.push({ type: 'shock', x: ix, y: iy, t: 0, life: 0.55, r: (area ?? 1.6) * 26, col });
        this.fx.push({
          type: 'crack',
          x: ix,
          y: iy,
          t: 0,
          life: 1.4,
          r: (area ?? 1.4) * 22,
          seed: R() * 999,
        });
        this.fx.push({ type: 'beam', x: sx, y: sy, t: 0, life: 0.45, w: 22, col });
        this.burst(ix, iy - ch, 30, [`rgb(${col})`, '#ffffff'], 120, 50, 0.7);
        this.impact(
          col,
          s.kind === 'power' ? 0.45 : 0.28,
          s.kind === 'power' ? 1 : 0.6,
          s.kind === 'power' ? 8 : 5,
        );
      } else this.impact(col, 0.1, 0.25, 2);
    } else if (s.kind === 'buff' || s.kind === 'heal') {
      this.fx.push({ type: 'beam', x: sx, y: sy, t: 0, life: 0.6, w: 18, col });
      this.fx.push({ type: 'shock', x: sx, y: sy, t: 0, life: 0.5, r: 30, col });
    }
    // The move's own effect (render/fx/<fx>.ts); one with no file of its own ('ring') has only the
    // shared ones above.
    this.skillFx.of(s.fx)?.skill?.({
      src,
      srcId,
      tgt,
      s,
      look,
      style,
      sx,
      sy,
      tx,
      ty,
      spot,
      volley,
      humanoid,
      brk,
    });
    this.trail = null;
  }

  /** A move's own mark beside its category's effect (skillFx.ts `FxExtra`). */
  private extraMark(
    extra: NonNullable<SkillLook['extra']>,
    src: Entity,
    tgt: Entity | null | undefined,
    s: SkillDef,
    spot: [number, number] | null,
    element: string,
  ): void {
    const [sx, sy] = this.pos(src);
    const f = this.iso.facing(src);
    const at = (e: Entity | null | undefined): [number, number] =>
      e ? this.pos(e) : (spot ?? [sx + f * 24, sy + 6]);
    const push = (x: number, y: number, life: number, spec: SheetSpec, t = 0) =>
      this.fx.push({ type: 'sheet', x, y, t, life, s: spec });
    switch (extra) {
      case 'shackle': {
        // Chains close round the target's body.
        if (!tgt) return;
        const [x, y] = this.pos(tgt);
        push(x, y - 12 * this.tall(tgt), 0.75, { kind: 'shackle', tint: 'metal', size: tgt.scale }, -0.05);
        break;
      }
      case 'sound':
        // The qin's notes spreading out ahead.
        push(sx + f * 6, sy - 18 * this.tall(src), 0.7, {
          kind: 'sound',
          tint: element,
          size: 1,
          flip: f < 0,
        });
        break;
      case 'smoke':
        push(sx, sy, 0.9, { kind: 'smoke', tint: '132,128,138', size: src.scale });
        this.dust(sx, sy, 10, 10, 40);
        break;
      case 'caltrops': {
        const [x, y] = at(tgt);
        push(x, y, 1.4, { kind: 'caltrops', tint: 'metal', size: 1 });
        break;
      }
      case 'dharma': {
        // The wheel turns where the palm comes down: the spot picked, round the caster, or the foe.
        const [x, y] = spot ?? (s.aoe || !tgt ? [sx, sy] : this.pos(tgt));
        const r = Math.max(24, Math.round(((s.aoe ?? 1.4) * 24) / 12) * 12);
        push(x, y, 1, { kind: 'dharma', tint: 'yang', size: 1, r });
        break;
      }
    }
  }

  /** The aura of the player's active neigong once mastered (null otherwise). */
  playerAura(): { style: string; color: string } | null {
    const w = this.world;
    const pr = w.progress;
    if (w.player.dead) return null;
    // A title's own aura (เถาฉางเยี่ย) outshines any neigong's.
    const titled = TITLES[pr.titleWorn]?.aura;
    if (titled) return titled;
    if (!neigongMastered(pr.neigong, pr.activeNeigong)) return null;
    return getNeigong(pr.activeNeigong).aura;
  }

  /** Particles of the mastery aura, in the neigong's own style. */
  private masteryAura(dt: number): void {
    const aura = this.playerAura();
    if (!aura) return;
    // Twice the motes of the plain style, and now and then a ring of qi bursting outward.
    this.masteryMotes(dt);
    this.auraPulseT -= dt;
    if (this.auraPulseT <= 0) {
      this.auraPulseT = 1.6;
      const p = this.world.player;
      const [x, y] = this.pos(p);
      this.qiWisps(x, y, p, aura.color, 10);
    }
  }

  private masteryMotes(dt: number): void {
    const aura = this.playerAura();
    if (!aura) return;
    const p = this.world.player;
    const [x, y0] = this.pos(p);
    const y = y0 - p.z - this.hoverOf(p);
    const h = 30 * this.tall(p);
    const col = aura.color;
    this.auraT -= dt;
    this.auraBigT -= dt;
    const every = (t: number) => {
      if (this.auraT > 0) return false;
      this.auraT = t * 0.5;
      return true;
    };
    const big = (t: number) => {
      if (this.auraBigT > 0) return false;
      this.auraBigT = t;
      return true;
    };
    const mote = (o: Partial<Particle>) =>
      this.parts.push({ x, y, vx: 0, vy: 0, g: 0, t: 0, life: 0.8, c: `rgb(${col})`, ...o });
    switch (aura.style) {
      case 'flame':
        // Blood flames licking up the body.
        if (every(0.025))
          mote({
            x: x + (R() - 0.5) * 18,
            y: y - R() * h * 0.6,
            vx: (R() - 0.5) * 10,
            vy: -(30 + R() * 40),
            g: -30,
            life: 0.4 + R() * 0.3,
            c: R() < 0.3 ? '#ffb070' : R() < 0.6 ? `rgb(${col})` : '#7a1418',
            s: R() < 0.4 ? 2 : 1,
            drag: 1,
          });
        break;
      case 'mist':
        // Cloud wreathing the feet.
        if (every(0.14))
          this.fx.push({
            type: 'puff',
            x: x + (R() - 0.5) * 26,
            y: y + (R() - 0.5) * 4,
            t: 0,
            life: 1.1,
            r: 5 + R() * 4,
          });
        break;
      case 'taiji':
        if (big(1.3)) this.fx.push({ type: 'yinyang', x, y, t: 0, life: 1.5, r: 18 });
        if (every(0.08)) {
          const a = this.world.time * 3 + (R() < 0.5 ? 0 : Math.PI);
          mote({
            x: x + Math.cos(a) * 14,
            y: y - 6 + Math.sin(a) * 6,
            vy: -10,
            life: 0.6,
            c: R() < 0.5 ? '#ffffff' : '#20202a',
          });
        }
        break;
      case 'bell':
        // A golden bell of light ringing out from the body, motes drifting up.
        if (big(1.1)) this.fx.push({ type: 'ring', x, y, t: 0, life: 0.9, r: 20, col });
        if (every(0.07))
          mote({
            x: x + (R() - 0.5) * 22,
            y: y - R() * h,
            vy: -(12 + R() * 14),
            life: 0.9,
            s: R() < 0.3 ? 2 : 1,
          });
        break;
      case 'blades':
        // Glints of sword light circling the guardian.
        if (every(0.05)) {
          const a = this.world.time * 5 + R() * 0.4;
          mote({
            x: x + Math.cos(a) * 16,
            y: y - h * 0.45 + Math.sin(a) * 7,
            vx: -Math.sin(a) * 60,
            vy: Math.cos(a) * 28,
            life: 0.22,
            c: R() < 0.5 ? '#ffffff' : `rgb(${col})`,
            streak: true,
          });
        }
        break;
      case 'petals':
        if (every(0.09))
          mote({
            x: x + (R() - 0.5) * 30,
            y: y - h - R() * 10,
            vx: (R() - 0.5) * 16,
            vy: 14 + R() * 10,
            life: 1.2,
            c: R() < 0.5 ? `rgb(${col})` : '#f0c8ff',
            s: 2,
          });
        break;
      case 'bloodstorm': {
        // Blood rising off the body like steam and dripping back, red lightning cracking round it.
        if (every(0.03))
          mote({
            x: x + (R() - 0.5) * 22,
            y: y - R() * h * 0.7,
            vx: (R() - 0.5) * 14,
            vy: -(20 + R() * 30),
            g: 60,
            life: 0.6 + R() * 0.3,
            c: R() < 0.35 ? '#5a0408' : R() < 0.75 ? '#c8101c' : '#ff4a50',
            s: R() < 0.4 ? 2 : 1,
          });
        // Haki: black-cored red lightning crackling out of the body, forking as it goes.
        const zap = (reach: number, life: number, w: number) => {
          const a = R() * Math.PI * 2;
          const sx0 = x + Math.cos(a) * 5;
          const sy0 = y - h * (0.25 + R() * 0.65);
          const ex = x + Math.cos(a) * reach;
          const ey = sy0 + Math.sin(a) * reach * 0.5 + (R() - 0.5) * 8;
          this.fx.push({
            type: 'zap',
            x: sx0,
            y: sy0,
            t: 0,
            life,
            x1: ex,
            y1: ey,
            col: '255,20,30',
            core: '12,0,2',
            seed: R() * 999,
            w,
          });
          // A fork off the middle of the bolt.
          const [mx, my] = [(sx0 + ex) / 2, (sy0 + ey) / 2];
          this.fx.push({
            type: 'zap',
            x: mx,
            y: my,
            t: 0,
            life: life * 0.8,
            x1: mx + (R() - 0.5) * reach * 0.5,
            y1: my + (R() - 0.5) * reach * 0.4,
            col: '255,30,40',
            core: '12,0,2',
            seed: R() * 999,
            w: w * 0.6,
          });
        };
        // Kept inside the circle round the feet.
        if (big(0.11)) zap(14 + R() * 8, 0.14, 4);
        this.hakiT -= dt;
        if (this.hakiT <= 0) {
          this.hakiT = 1.5;
          for (let n = 0; n < 4; n++) zap(16 + R() * 6, 0.2, 5);
        }
        if (every(0.05))
          mote({
            x: x + (R() - 0.5) * 26,
            y: y - R() * h,
            vx: (R() - 0.5) * 10,
            vy: -(18 + R() * 20),
            life: 0.7,
            c: R() < 0.6 ? '#140204' : '#5a0408',
            s: 2,
            drag: 1,
          });
        break;
      }
      case 'swordink': {
        // The Sword Demon: black ink smoke boiling off the body, streaks of sword intent cutting
        // through it, and now and then a red glint.
        if (every(0.012))
          mote({
            x: x + (R() - 0.5) * 60,
            y: y - R() * h * 0.6,
            vx: (R() - 0.5) * 24,
            vy: -(16 + R() * 26),
            life: 0.9 + R() * 0.4,
            c: R() < 0.7 ? '#0c0c10' : '#2e2e38',
            s: R() < 0.6 ? 3 : 2,
            drag: 1.5,
          });
        if (big(0.18)) {
          const a = R() * Math.PI * 2;
          this.fx.push({
            type: 'cut',
            x: x + Math.cos(a) * 6,
            y: y - h * (0.3 + R() * 0.6),
            t: 0,
            life: 0.22,
            x1: x + Math.cos(a) * 26,
            y1: y - h * 0.5 + Math.sin(a) * 12,
            col: R() < 0.25 ? '220,30,40' : '230,230,240',
          });
        }
        break;
      }
      case 'ink':
        // Ink swirling round the scholar like brush strokes.
        if (every(0.04)) {
          const a = this.world.time * 2.4 + R() * 6.28;
          const r = 12 + R() * 6;
          mote({
            x: x + Math.cos(a) * r,
            y: y - R() * h * 0.8 + Math.sin(a) * 4,
            vx: -Math.sin(a) * 20,
            vy: -8,
            life: 0.7,
            c: R() < 0.7 ? `rgb(${col})` : '#ffffff',
            s: R() < 0.4 ? 2 : 1,
            drag: 2,
          });
        }
        break;
    }
  }

  /** Blood spraying out of a point: heavy drops that arc and fall. */
  bloodSpray(x: number, y: number, n: number, spd: number): void {
    for (let k = 0; k < n; k++) {
      const a = R() * Math.PI * 2;
      const v = spd * (0.3 + R() * 0.7);
      this.parts.push({
        x,
        y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v * 0.6 - 40,
        g: 260,
        t: 0,
        life: 0.4 + R() * 0.4,
        c: R() < 0.3 ? '#5a0408' : R() < 0.7 ? '#b0101c' : '#e8303a',
        s: R() < 0.4 ? 2 : 1,
      });
    }
  }

  /** Qi rising off a fighter like heat: coloured motes streaming up from the feet past the head. */
  private qiWisps(x: number, y: number, e: Entity, col: string, n: number): void {
    const h = 30 * this.tall(e) * e.scale;
    for (let k = 0; k < n; k++) {
      const a = R() * Math.PI * 2;
      this.parts.push({
        x: x + Math.cos(a) * (6 + R() * 6) * e.scale,
        y: y + Math.sin(a) * 3 - R() * h * 0.5,
        vx: (R() - 0.5) * 14,
        vy: -(40 + R() * 70),
        g: -20,
        t: 0,
        life: 0.45 + R() * 0.4,
        c: R() < 0.35 ? '#ffffff' : `rgb(${col})`,
        s: R() < 0.3 ? 2 : 1,
        streak: R() < 0.5,
        drag: 1.5,
      });
    }
  }

  /** Height of an entity's figure on screen (world pixels). */
  figH(e: Entity): number {
    const set = this.sprites.of(e);
    return (set?.h ?? 40) * visualScale(e, set);
  }

  /** Effects laid on the ground (frost), drawn under the figures. */
  drawGround(g: CanvasRenderingContext2D, ox: number, oy: number): void {
    for (const f of this.fx) {
      if (f.type === 'sheet' && f.ground && f.t >= 0)
        drawSheet(g, f.s, fxStep(f.t) / f.life, f.x + ox, f.y + oy);
      else if (f.type === 'custom' && f.ground && f.t >= 0)
        f.draw(g, f.x + ox, f.y + oy, fxStep(f.t) / f.life, ox, oy);
    }
  }

  /**
   * The ice a frozen foe stands in, drawn right after the foe (renderer.ts) at its feet (`x`, `y`,
   * where the foe itself was drawn, shaking with it): whatever a skill's effect holds round a figure.
   */
  drawHeld(g: CanvasRenderingContext2D, e: Entity, x: number, y: number): void {
    this.skillFx.drawHeld(g, e, x, y);
  }

  /** A spray of snowflakes, or with `ice` hard ice shards that fall. */
  snowBurst(x: number, y: number, n: number, ice: boolean): void {
    for (let k = 0; k < n; k++) {
      const a = R() * Math.PI * 2;
      const sp = (ice ? 90 : 50) * (0.4 + R() * 0.6);
      this.parts.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp * 0.6 - (ice ? 40 : 15),
        g: ice ? 220 : 25,
        t: 0,
        life: 0.5 + R() * 0.5,
        c: ice ? (R() < 0.5 ? '#bfe9ff' : '#e8fbff') : R() < 0.7 ? '#ffffff' : '#cfe8ff',
        s: ice || R() < 0.3 ? 2 : 1,
        drag: ice ? 0.5 : 2,
      });
    }
  }

  update(dt: number): void {
    const w = this.world;
    this.stepCloth(Math.min(dt, 0.05));
    for (const [id, b] of this.bubbles) if ((b.t += dt) > Effects.BUBBLE_LIFE) this.bubbles.delete(id);
    const p = w.player;
    // The player's afterimages, and (in a bout) the rival's, left on every fast move (a step, an air dash, an escape, a dive).
    const pb = w.pvpBout;
    const rival = pb ? w.get(pb.a === p.id ? pb.b : pb.a) : undefined;
    for (const e of rival && !rival.dead ? [p, rival] : [p]) {
      const own = e === p;
      const kicking = e.z > 0 && w.time - this.anim(e.id).flipAt < 0.4;
      if (!(e.dashT > 0 || kicking)) {
        if (own) this.ghostN = 0;
        else this.foeGhostN = 0;
        continue;
      }
      if ((own ? this.ghostN : this.foeGhostN) >= 3) continue;
      const left = own ? this.ghostT : this.foeGhostT;
      const next = left - dt;
      if (own) this.ghostT = next;
      else this.foeGhostT = next;
      if (next > 0) continue;
      const wait = e.dashT > 0 ? 0.06 : 0.08;
      if (own) this.ghostN++;
      else this.foeGhostN++;
      if (own) this.ghostT = wait;
      else this.foeGhostT = wait;
      const sp = this.sprites.of(e);
      if (!sp) continue;
      const { set, facing } = viewOf(sp, this.iso.heading(e), this.iso.facing(e));
      const [x, y] = this.pos(e);
      // The pose it is in right now, in the view it is drawn in (render/lastShot.ts; the frame canvas is kept, its ghost
      // made once by ghostOf), else the dash still.
      const cur = lastShotOf(e.id, w.time);
      this.fx.push({
        type: 'ghost',
        step: true,
        x,
        y: y - e.z,
        t: 0,
        // The locked split-body look (taiji sample): 3 copies, 0.4 s, solid then stepped away.
        life: 0.4,
        img: cur?.img ?? set.poses.dash ?? set.walk[1]!,
        flip: (cur ? cur.facing : facing) < 0,
        ax: set.ax,
        ay: set.ay,
      });
    }
    this.masteryAura(dt);
    // Whirlwinds in progress: a red tornado of streaks and blood swirling round the spinner.
    for (const e of w.entities.values()) {
      if (e.spinT <= 0 || e.dead) continue;
      const [x, y] = this.pos(e);
      for (let k = 0; k < 3; k++) {
        const a = w.time * 14 + k * 2.1 + R() * 0.4;
        const r = 18 + R() * 26;
        this.parts.push({
          x: x + Math.cos(a) * r,
          y: y - 6 - R() * 34 + Math.sin(a) * r * 0.4,
          vx: -Math.sin(a) * 180,
          vy: Math.cos(a) * 70,
          g: 0,
          t: 0,
          life: 0.16,
          c: R() < 0.5 ? '#e8303a' : R() < 0.5 ? '#ffd0d0' : '#7a0810',
          streak: true,
        });
      }
    }
    // Sparks of blue light drifting up from the warp circles.
    for (const P of w.map.portals) {
      if (R() >= dt * 14) continue;
      const [px, py] = this.iso.proj(P.x, P.y);
      const a = R() * Math.PI * 2;
      this.parts.push({
        x: px + Math.cos(a) * 24,
        y: py + Math.sin(a) * 12,
        vx: 0,
        vy: -(30 + R() * 30),
        g: 0,
        t: 0,
        life: 1,
        c: R() < 0.5 ? '#ffffff' : '#8fd0ff',
        s: R() < 0.3 ? 2 : 1,
      });
    }
    // เปิดแดง: blood dripping and a red mist rising off anyone with PK on.
    this.pkT -= dt;
    if (this.pkT <= 0) {
      this.pkT = 0.05;
      for (const e of w.entities.values()) {
        if (!e.pk || e.dead) continue;
        const [x, y] = this.pos(e);
        const top = y - e.z - this.hoverOf(e);
        this.parts.push({
          x: x + (R() - 0.5) * 22,
          y: top - 10 - R() * 30,
          vx: (R() - 0.5) * 8,
          vy: R() < 0.5 ? -(20 + R() * 20) : 20,
          g: R() < 0.5 ? 0 : 260,
          t: 0,
          life: 0.6,
          c: R() < 0.5 ? '#c8101c' : R() < 0.5 ? '#ff4a50' : '#5a0408',
          s: R() < 0.4 ? 2 : 1,
        });
      }
    }
    // Blood dripping off the great sabres of the Blood Sabre school.
    this.dripT -= dt;
    if (this.dripT <= 0) {
      this.dripT = 0.09;
      for (const e of w.entities.values()) {
        if (e.dead || !(e.sprite.startsWith('heroBlood') || e.sprite === 'bloodMaster')) continue;
        if (Math.abs(e.x - p.x) + Math.abs(e.y - p.y) > 24 || R() < 0.35) continue;
        const [x, y] = this.pos(e);
        const set = this.sprites.of(e);
        const h = (set?.h ?? 40) * visualScale(e, set);
        const f = this.iso.facing(e) < 0 ? -1 : 1;
        const dx = -8 + R() * 30;
        this.parts.push({
          x: x + dx * f,
          y: y - e.z - this.hoverOf(e) - h * 0.52 + 3,
          vx: 0,
          vy: 10,
          g: 320,
          t: 0,
          life: 0.45,
          c: R() < 0.5 ? '#b0101c' : '#7a0810',
          s: R() < 0.3 ? 2 : 1,
        });
      }
    }
    if (w.progress.skinWorn.back && !p.dead) {
      this.skinT -= dt;
      if (this.skinT <= 0) {
        this.skinT = 0.07;
        const [x, y] = this.pos(p);
        const set = this.sprites.of(p);
        const top = y - p.z - this.hoverOf(p) - (set?.h ?? 40) * 0.5 - 26;
        this.parts.push({
          x: x + (R() - 0.5) * 36,
          y: top + R() * 18,
          vx: (R() - 0.5) * 6,
          vy: -(10 + R() * 14),
          g: 0,
          t: 0,
          life: 0.7,
          c: R() < 0.4 ? '#f0e0ff' : '#b068ff',
          drag: 1,
        });
      }
    }
    for (const q of this.swordTrail) q.t += dt;
    this.swordTrail = this.swordTrail.filter((q) => q.t < 0.5);
    if (p.mount) {
      const [x, y] = this.pos(p);
      const hy = y - p.z - this.hoverOf(p);
      // Motes of qi drifting up around the blade, moving or not.
      this.rideSparkT -= dt;
      if (this.rideSparkT <= 0) {
        this.rideSparkT = 0.05;
        this.parts.push({
          x: x + (R() - 0.5) * 30,
          y: hy + (R() - 0.5) * 6,
          vx: (R() - 0.5) * 8,
          vy: -(14 + R() * 22),
          g: 0,
          t: 0,
          life: 0.5 + R() * 0.4,
          c: R() < 0.5 ? '#ffffff' : '#9fe0ff',
          drag: 1,
        });
      }
    }
    if (p.mount && p.moving) {
      // Riding the flying sword: a stream of qi off the tail of the blade.
      const [x, y] = this.pos(p);
      const ha = (this.iso.heading(p) * Math.PI) / 4;
      const [dx, dy] = [Math.cos(ha), Math.sin(ha) * 0.5];
      const hy = y - p.z - this.hoverOf(p);
      // A ribbon of sword light left along the path, starting at the hilt.
      this.swordTrail.push({ x: x - dx * 13, y: hy + 1 - dy * 13, t: 0 });
      // Faint afterimages of the rider, and wind tearing past.
      this.rideGhostT -= dt;
      if (this.rideGhostT <= 0) {
        this.rideGhostT = 0.08;
        const { set, facing } = viewOf(this.sprites.of(p)!, this.iso.heading(p), this.iso.facing(p));
        this.fx.push({
          type: 'ghost',
          x,
          y: hy,
          t: 0.14,
          life: 0.36,
          img: set.idle[0]!,
          flip: facing < 0,
          ax: set.ax,
          ay: set.ay,
        });
        for (let k = 0; k < 2; k++)
          this.parts.push({
            x: x + (R() - 0.5) * 26 + dx * 10,
            y: hy - 8 - R() * 50,
            vx: -dx * 220,
            vy: -dy * 220,
            g: 0,
            t: 0,
            life: 0.16,
            c: 'rgba(225,245,255,.85)',
            streak: true,
          });
      }
      // Heading toward the viewer, the tail is behind the body (and effects draw over it), so the
      // qi sprays out sideways at the feet instead of streaming up across the robe.
      const toward = dy > 0.15;
      for (let k = 0; k < 2; k++)
        this.parts.push({
          x: x - (toward ? 0 : dx * 12) + (R() - 0.5) * 3,
          y: hy + (toward ? 2 : -dy * 12) + (R() - 0.5) * 2,
          vx: toward ? (k ? 1 : -1) * (40 + R() * 30) : -dx * (50 + R() * 40),
          vy: toward ? R() * 6 : -dy * (50 + R() * 40) - 6,
          g: 0,
          t: 0,
          life: 0.25 + R() * 0.2,
          c: R() < 0.4 ? '#ffffff' : 'rgba(150,225,255,.9)',
          streak: R() < 0.6,
          drag: 3,
        });
    }
    if (p.sprinting) {
      // Qinggong run: faint afterimages and wind streaks.
      this.sprintGhostT -= dt;
      if (this.sprintGhostT <= 0) {
        this.sprintGhostT = 0.07;
        const { set, facing } = viewOf(this.sprites.of(p)!, this.iso.heading(p), this.iso.facing(p));
        const [x, y] = this.pos(p);
        this.fx.push({
          type: 'ghost',
          x,
          y: y - p.z,
          t: 0.12,
          life: 0.32,
          step: true,
          img: set.sprint?.[0] ?? set.walk[1]!,
          flip: facing < 0,
          ax: set.ax,
          ay: set.ay,
        });
        this.parts.push({
          x: x + (R() - 0.5) * 20,
          y: y - 10 - R() * 40,
          vx: -this.iso.facing(p) * 140,
          vy: 0,
          g: 0,
          t: 0,
          life: 0.18,
          c: 'rgba(230,240,255,.8)',
          streak: true,
        });
      }
    }
    if (p.healT > 0 && R() < dt * 30) {
      // Golden motes spiralling up around the body.
      const [x, y] = this.pos(p);
      const a = w.time * 6 + R();
      this.parts.push({
        x: x + Math.cos(a) * 12,
        y: y - R() * 6 + Math.sin(a) * 4,
        vx: -Math.sin(a) * 14,
        vy: -30 - R() * 20,
        g: 0,
        t: 0,
        life: 0.8,
        c: R() < 0.5 ? '#ffe08a' : '#fff6d0',
      });
    }
    if (p.meditating && R() < dt * 12) {
      const [x, y] = this.pos(p);
      this.parts.push({
        x: x + (R() - 0.5) * 26,
        y: y - R() * 4,
        vx: 0,
        vy: -18 - R() * 10,
        g: 0,
        t: 0,
        life: 1.2,
        c: R() < 0.5 ? '#9ff0d0' : '#e4fff4',
      });
    }
    for (const e of w.entities.values()) {
      if (e.dead) continue;
      // Footstep puffs while running on the ground.
      if (e.moving && e.z <= 0 && (e.kind === 'player' || e.kind === 'monster')) {
        const a = this.anim(e.id);
        a.stepT -= dt;
        if (a.stepT <= 0) {
          a.stepT = e.dashT > 0 ? 0.05 : 0.25;
          const [x, y] = this.pos(e);
          this.dust(x - this.iso.facing(e) * 3, y, e.dashT > 0 ? 3 : 2, 2, 12);
        }
      }
      if (!e.cast || R() > dt * 50) continue;
      // Qi converging on a charging fighter.
      const [x, y] = this.pos(e);
      const a = R() * Math.PI * 2;
      const col = e.cast.kind === 'break' ? '#ffa040' : e.cast.kind === 'heal' ? '#ffe08a' : '#ff7a6a';
      this.parts.push({
        x: x + Math.cos(a) * 18 * e.scale,
        y: y - 14 * e.scale * this.tall(e) + Math.sin(a) * 10,
        vx: -Math.cos(a) * 45,
        vy: -Math.sin(a) * 26,
        g: 0,
        t: 0,
        life: 0.35,
        c: col,
        streak: true,
      });
    }
    for (const id of this.anims.keys()) if (!w.get(id)) this.anims.delete(id);

    for (const s of this.swords) {
      s.t += dt;
      const src = w.get(s.srcId);
      if (s.t < s.launch && src) {
        const [px, py] = this.pos(src);
        const a = s.ang + s.t * 5;
        s.x = px + Math.cos(a) * 16;
        s.y = py - 22 * this.tall(src) + Math.sin(a) * 7;
        s.fx = s.x;
        s.fy = s.y;
        s.rot = -Math.PI / 2;
        continue;
      }
      const tgt = w.get(s.tgtId);
      let tx: number;
      let ty: number;
      if (tgt) {
        [tx, ty] = this.pos(tgt);
        ty -= 10 * tgt.scale * this.tall(tgt);
      } else {
        tx = s.fx + Math.cos(s.ang) * 60;
        ty = s.fy + Math.sin(s.ang) * 30;
      }
      const k = Math.min(1, (s.t - s.launch) / (s.arrive - s.launch));
      const nx = s.fx + (tx - s.fx) * k;
      const ny = s.fy + (ty - s.fy) * k;
      s.rot = Math.atan2(ny - s.y, nx - s.x) || s.rot;
      s.x = nx;
      s.y = ny;
      this.parts.push({ x: s.x, y: s.y, vx: 0, vy: 0, g: 0, t: 0, life: 0.2, c: '#7fd8ff' });
    }
    this.swords = this.swords.filter((s) => s.t < s.arrive);

    for (const f of this.fx) {
      f.t += dt;
      if (f.type === 'wave' && R() < dt * 60) {
        const k = Math.min(1, f.t / f.life);
        this.parts.push({
          x: f.x + (f.x1 - f.x) * k,
          y: f.y + (f.y1 - f.y) * k,
          vx: (R() - 0.5) * 30,
          vy: (R() - 0.5) * 20,
          g: 10,
          t: 0,
          life: 0.4,
          c: R() < 0.5 ? '#8fe6a8' : '#e6ffe6',
        });
      }
    }
    this.skillFx.step(dt);
    this.fx = this.fx.filter((f) => f.t < f.life);
    // FX-PIXEL: particles move in held steps (fx/pixelRules.ts `PART_FPS`).
    let pdt = dt;
    if (fxPixelOn()) {
      this.partAcc += dt;
      pdt = Math.floor(this.partAcc * PART_FPS) / PART_FPS;
      this.partAcc -= pdt;
    }
    for (const q of this.parts) {
      q.t += pdt;
      // Not out yet (waiting for its blow to land).
      if (q.t < 0) continue;
      if (q.drag) {
        const d = Math.max(0, 1 - q.drag * pdt);
        q.vx *= d;
        q.vy *= d;
      }
      q.vy += q.g * pdt;
      q.x += q.vx * pdt;
      q.y += q.vy * pdt;
    }
    this.parts = this.parts.filter((q) => q.t < q.life);
    // The graphics tier's cap (graphics.ts): the oldest go first.
    if (this.parts.length > particleCap()) this.parts.splice(0, this.parts.length - particleCap());
    for (const n of this.texts) n.t += dt;
    this.texts = this.texts.filter((n) => n.t < n.life);
    this.shake = Math.max(0, this.shake - dt * 20);
    this.flash.a = Math.max(0, this.flash.a - dt * 2.2);
    this.frost = Math.max(0, this.frost - dt * 1.3);
    this.punch = Math.max(0, this.punch - dt * 5);
    this.cineT = Math.max(0, this.cineT - dt);
    if (this.stamp && (this.stamp.t += dt) >= this.stamp.life) this.stamp = null;
  }

  /**
   * Enemy wind-ups (the combat triangle's tell): the ground each gathering blow will strike, red for
   * a true attack and amber with a jagged reach for a break (破), filling from the attacker as the
   * blow gathers. Ground-circle telegraphs are the renderer's.
   */
  private drawTells(g: CanvasRenderingContext2D, ox: number, oy: number): void {
    const w = this.world;
    for (const e of w.entities.values()) {
      const c = e.cast;
      if (!c || e.dead || e.kind === 'player' || e.kind === 'pet' || c.kind === 'heal' || c.kind === 'buff')
        continue;
      const s = SKILLS[c.skillId];
      if (!s || s.telegraph) continue;
      const [x, y] = this.pos(e);
      const tgt = c.targetId === null ? null : w.get(c.targetId);
      const [tx, ty] = c.at
        ? this.iso.proj(c.at.x, c.at.y)
        : tgt
          ? this.pos(tgt)
          : [x + this.iso.facing(e), y];
      const full = !!s.aoe && !s.ground;
      const reach = (full ? s.aoe! : Math.max(1.4, Math.min(3.2, s.range))) * 22.6 * Math.min(1.4, e.scale);
      drawTell(
        g,
        Math.min(1, c.t / c.windup),
        x + ox,
        y + oy,
        tx - x,
        ty - y,
        reach,
        c.kind === 'break',
        full,
      );
    }
  }

  /** Draws world-space effects onto the low-res canvas. */
  draw(g: CanvasRenderingContext2D, ox: number, oy: number): void {
    const iso = (x: number, y: number, rx: number, ry: number) => {
      g.beginPath();
      g.ellipse(x, y, Math.max(0.5, rx), Math.max(0.5, ry), 0, 0, Math.PI * 2);
    };
    this.drawTells(g, ox, oy);
    for (const f of this.fx) {
      if (f.t < 0) continue; // delayed
      // FX-PIXEL: held frames (fx/pixelRules.ts `fxStep`).
      const ft = fxStep(f.t);
      const k = ft / f.life;
      const x = f.x + ox;
      const y = f.y + oy;
      switch (f.type) {
        case 'arc':
          drawArc(g, x, y, f, k);
          break;
        case 'sheet': {
          if (f.ground) break;
          // Cached frames on the effect's phase; a projectile carried to its mark as it plays.
          const m = f.x1 === undefined ? 0 : Math.min(1, k / 0.9);
          drawSheet(g, f.s, k, x + ((f.x1 ?? f.x) - f.x) * m, y + ((f.y1 ?? f.y) - f.y) * m);
          break;
        }
        case 'thrust': {
          // A spear of light shooting forward, thinning as it fades, with speed lines.
          const grow = Math.min(1, k * 4);
          const len = f.len * grow;
          const x0 = f.f > 0 ? x + 8 : x - 8 - len;
          const a = 1 - k;
          g.fillStyle = `rgba(${f.col},${0.55 * a})`;
          g.fillRect(Math.round(x0), Math.round(y - 2), Math.round(len), 5);
          g.fillStyle = `rgba(255,255,255,${a})`;
          g.fillRect(Math.round(x0), Math.round(y - 1), Math.round(len), k < 0.5 ? 3 : 1);
          const tip = f.f > 0 ? x0 + len : x0;
          g.fillRect(Math.round(tip - 1), Math.round(y - 3), 2, 7);
          for (let s = 0; s < 3; s++) {
            const ly = y - 7 + s * 7;
            const l = len * (0.4 + s * 0.15);
            g.fillStyle = `rgba(${f.col},${0.6 * a})`;
            g.fillRect(
              Math.round(f.f > 0 ? x0 + len * 0.2 : x0 + len * 0.8 - l),
              Math.round(ly),
              Math.round(l),
              1,
            );
          }
          break;
        }
        case 'wave': {
          // Travelling crescent of wind.
          const t = Math.min(1, k / 0.9);
          const wx = f.x + (f.x1 - f.x) * t + ox;
          const wy = f.y + (f.y1 - f.y) * t + oy;
          const ang = Math.atan2(f.y1 - f.y, f.x1 - f.x);
          g.save();
          g.translate(wx, wy);
          g.rotate(ang);
          const a = k < 0.85 ? 1 : (1 - k) / 0.15;
          g.fillStyle = `rgba(${f.col},${0.55 * a})`;
          g.beginPath();
          g.ellipse(0, 0, 5, 11, 0, -Math.PI / 2, Math.PI / 2);
          g.ellipse(-3, 0, 3, 9, 0, Math.PI / 2, -Math.PI / 2, true);
          g.fill();
          g.strokeStyle = `rgba(255,255,255,${0.9 * a})`;
          g.lineWidth = 1;
          g.beginPath();
          g.ellipse(0, 0, 5, 11, 0, -Math.PI / 2, Math.PI / 2);
          g.stroke();
          g.restore();
          break;
        }
        case 'bolt': {
          const r = mulberry32((f.seed + Math.floor(ft * 30)) | 0);
          g.strokeStyle = `rgba(190,235,255,${1 - k})`;
          g.lineWidth = 3;
          for (let pass = 0; pass < 2; pass++) {
            g.beginPath();
            let py = y - 150;
            g.moveTo(x, py);
            for (let s = 0; s < 10; s++) {
              py += 15;
              g.lineTo(x + (r() - 0.5) * 14 * (1 - s / 10), s === 9 ? y - 6 : py);
            }
            g.stroke();
            g.strokeStyle = `rgba(255,255,255,${1 - k})`;
            g.lineWidth = 1;
          }
          g.fillStyle = `rgba(180,230,255,${0.5 * (1 - k)})`;
          iso(x, y, 20, 9);
          g.fill();
          break;
        }
        case 'yinyang':
          drawYinYang(
            g,
            x,
            y,
            f.r * (0.7 + 0.3 * Math.min(1, k * 3)),
            ft * 7,
            k < 0.6 ? 0.85 : (1 - k) * 2.1,
          );
          break;
        case 'ring':
          g.strokeStyle = `rgba(${f.col},${1 - k})`;
          g.lineWidth = 2;
          iso(x, y, f.r * k + 2, (f.r * k + 2) / 2);
          g.stroke();
          break;
        case 'gather': {
          // A ring on the ground closing in while a skill charges.
          const r = f.r * (1 - k) + 6;
          g.strokeStyle = `rgba(${f.col},${0.3 + 0.6 * k})`;
          g.lineWidth = 1;
          iso(x, y, r, r / 2);
          g.stroke();
          g.fillStyle = `rgba(${f.col},${0.12 + 0.18 * k})`;
          iso(x, y, 8 + 4 * k, 4 + 2 * k);
          g.fill();
          break;
        }
        case 'sigil': {
          // A turning circle of runes on the ground.
          const a = k < 0.15 ? k / 0.15 : k > 0.7 ? (1 - k) / 0.3 : 1;
          const r = f.r * (0.8 + 0.2 * Math.min(1, k * 4));
          g.strokeStyle = `rgba(${f.col},${0.75 * a})`;
          g.lineWidth = 1;
          iso(x, y, r, r / 2);
          g.stroke();
          iso(x, y, r * 0.7, r * 0.35);
          g.stroke();
          g.fillStyle = `rgba(${f.col},${0.9 * a})`;
          for (let s = 0; s < 8; s++) {
            const ang = ft * 1.8 + (s * Math.PI) / 4;
            const rr = r * 0.85;
            g.fillRect(Math.round(x + Math.cos(ang) * rr), Math.round(y + Math.sin(ang) * rr * 0.5), 2, 1);
          }
          break;
        }
        case 'shock': {
          // A thick ring that races outward, bright at its leading edge.
          const r = f.r * (0.2 + 0.8 * Math.sqrt(k));
          const a = 1 - k;
          g.lineWidth = Math.max(1, 6 * (1 - k));
          g.strokeStyle = `rgba(${f.col},${0.55 * a})`;
          iso(x, y, r, r / 2);
          g.stroke();
          g.lineWidth = 1;
          g.strokeStyle = `rgba(255,255,255,${0.9 * a})`;
          iso(x, y, r + 2, r / 2 + 1);
          g.stroke();
          g.fillStyle = `rgba(${f.col},${0.12 * a})`;
          iso(x, y, r, r / 2);
          g.fill();
          break;
        }
        case 'crack': {
          // Jagged dark fissures that fade; seeded so they hold their shape.
          const rr = mulberry32(f.seed | 0);
          const a = k < 0.7 ? 0.8 : ((1 - k) / 0.3) * 0.8;
          g.strokeStyle = `rgba(24,18,16,${a})`;
          g.lineWidth = 1;
          for (let n = 0; n < 7; n++) {
            let ang = rr() * Math.PI * 2;
            let px = x;
            let py = y;
            g.beginPath();
            g.moveTo(px, py);
            const segs = 3 + Math.floor(rr() * 3);
            for (let q = 0; q < segs; q++) {
              ang += (rr() - 0.5) * 0.9;
              const len = (f.r / segs) * (0.6 + rr() * 0.6) * Math.min(1, k * 8);
              px += Math.cos(ang) * len;
              py += Math.sin(ang) * len * 0.5;
              g.lineTo(Math.round(px) + 0.5, Math.round(py) + 0.5);
            }
            g.stroke();
          }
          break;
        }
        case 'beam': {
          // Light column: wide soft glow, bright core, fading at the top.
          const a = k < 0.2 ? k / 0.2 : 1 - (k - 0.2) / 0.8;
          // FX-PIXEL: a flat banded column (fx/pixelRules.ts), no gradient glow.
          drawColumn(g, x, y, 160, f.w, f.col, a);
          g.fillStyle = `rgba(${f.col},${0.3 * a})`;
          iso(x, y, f.w * 1.4, f.w * 0.7);
          g.fill();
          break;
        }
        case 'lines': {
          // Speed lines: short radial strokes flying outward from the hit.
          const rr = mulberry32(f.seed | 0);
          const a = 1 - k;
          g.lineWidth = 1;
          for (let n = 0; n < 12; n++) {
            const ang = rr() * Math.PI * 2;
            const r0 = f.r * (0.25 + k * 0.6);
            const r1 = r0 + f.r * (0.25 + rr() * 0.35) * (1 - k);
            g.strokeStyle = n % 3 ? `rgba(${f.col},${a})` : `rgba(255,255,255,${a})`;
            g.beginPath();
            g.moveTo(x + Math.cos(ang) * r0, y + Math.sin(ang) * r0 * 0.6);
            g.lineTo(x + Math.cos(ang) * r1, y + Math.sin(ang) * r1 * 0.6);
            g.stroke();
          }
          break;
        }
        case 'zap': {
          // Lightning: a jagged bolt re-drawn every few frames, bright core inside a coloured glow.
          const rr = mulberry32((f.seed + Math.floor(ft * 40)) | 0);
          const pts: [number, number][] = [[x, y]];
          const x1 = f.x1 + ox;
          const y1 = f.y1 + oy;
          for (let q = 1; q < 6; q++) {
            const u = q / 6;
            pts.push([x + (x1 - x) * u + (rr() - 0.5) * 8, y + (y1 - y) * u + (rr() - 0.5) * 8]);
          }
          pts.push([x1, y1]);
          const a = 1 - k;
          const gw = f.w ?? 3;
          for (const [w2, col] of [
            [gw, `rgba(${f.col},${0.55 * a})`],
            [Math.max(1, gw * 0.45), `rgba(${f.core ?? '255,220,220'},${a})`],
          ] as const) {
            g.strokeStyle = col;
            g.lineWidth = w2;
            g.beginPath();
            pts.forEach(([px, py], q) => (q ? g.lineTo(px, py) : g.moveTo(px, py)));
            g.stroke();
          }
          break;
        }
        case 'puff': {
          // Cloud puff: a grey underside and white top, swelling and fading.
          const r = f.r * (0.7 + k * 0.6);
          const a = k < 0.2 ? 0.9 : 0.9 * (1 - (k - 0.2) / 0.8);
          g.fillStyle = `rgba(190,205,225,${a})`;
          iso(x, y + 1, r, r * 0.45);
          g.fill();
          g.fillStyle = `rgba(255,255,255,${a})`;
          iso(x - 1, y - 1, r * 0.82, r * 0.38);
          g.fill();
          break;
        }
        case 'crescent': {
          // Sword qi in flight: a bright crescent with three ghosts trailing behind it.
          const ang = Math.atan2(f.y1 - f.y, f.x1 - f.x);
          const fade = k < 0.8 ? 1 : (1 - k) / 0.2;
          for (let q = 3; q >= 0; q--) {
            const tt = Math.max(0, Math.min(1, k / 0.85 - q * 0.09));
            const cx = f.x + (f.x1 - f.x) * tt + ox;
            const cy = f.y + (f.y1 - f.y) * tt + oy;
            const a = fade * (q ? 0.25 / q : 1);
            g.save();
            g.translate(cx, cy);
            g.rotate(ang);
            g.fillStyle = `rgba(${f.col},${0.6 * a})`;
            g.beginPath();
            g.ellipse(0, 0, f.r * 0.45, f.r, 0, -Math.PI / 2, Math.PI / 2);
            g.ellipse(-f.r * 0.3, 0, f.r * 0.3, f.r * 0.82, 0, Math.PI / 2, -Math.PI / 2, true);
            g.fill();
            if (!q) {
              g.strokeStyle = `rgba(255,255,255,${0.95 * a})`;
              g.lineWidth = 2;
              g.beginPath();
              g.ellipse(0, 0, f.r * 0.45, f.r, 0, -Math.PI / 2, Math.PI / 2);
              g.stroke();
            }
            g.restore();
          }
          break;
        }
        case 'cut': {
          // Flash cut: a bright line that snaps in full length, then thins and fades from the start.
          const a = 1 - k;
          const x1 = f.x1 + ox;
          const y1 = f.y1 + oy;
          const sx0 = x + (x1 - x) * Math.min(1, k * 1.6);
          const sy0 = y + (y1 - y) * Math.min(1, k * 1.6);
          g.lineCap = 'round';
          g.strokeStyle = `rgba(${f.col},${0.45 * a})`;
          g.lineWidth = 5 * a + 1;
          g.beginPath();
          g.moveTo(sx0, sy0);
          g.lineTo(x1, y1);
          g.stroke();
          g.strokeStyle = `rgba(255,255,255,${a})`;
          g.lineWidth = Math.max(1, 2 * a);
          g.beginPath();
          g.moveTo(sx0, sy0);
          g.lineTo(x1, y1);
          g.stroke();
          g.lineCap = 'butt';
          break;
        }
        case 'star': {
          // Impact flash: a four-point star that snaps open and shrinks.
          const r = f.r * (k < 0.3 ? 0.6 + k * 1.4 : 1.02 - (k - 0.3) * 1.2);
          const a = 1 - k * 0.7;
          g.fillStyle = `rgba(${f.col},${a})`;
          g.beginPath();
          g.moveTo(x, y - r);
          g.lineTo(x + r * 0.18, y - r * 0.18);
          g.lineTo(x + r, y);
          g.lineTo(x + r * 0.18, y + r * 0.18);
          g.lineTo(x, y + r);
          g.lineTo(x - r * 0.18, y + r * 0.18);
          g.lineTo(x - r, y);
          g.lineTo(x - r * 0.18, y - r * 0.18);
          g.closePath();
          g.fill();
          g.fillStyle = `rgba(255,255,255,${a})`;
          g.fillRect(Math.round(x - 1), Math.round(y - 1), 3, 3);
          break;
        }
        case 'shield': {
          // The guard flares where the blow lands.
          const a = 1 - k;
          const push = k * 3 * f.f;
          g.fillStyle = `rgba(${f.col},${0.35 * a})`;
          g.beginPath();
          g.ellipse(x + push, y, 5 + k * 3, f.h + k * 4, 0, -Math.PI / 2, Math.PI / 2, f.f < 0);
          g.fill();
          g.strokeStyle = `rgba(235,248,255,${a})`;
          g.lineWidth = 2;
          g.beginPath();
          g.ellipse(x + push, y, 5 + k * 3, f.h + k * 4, 0, -Math.PI / 2, Math.PI / 2, f.f < 0);
          g.stroke();
          break;
        }
        case 'pillar': {
          const a = k < 0.2 ? k / 0.2 : 1 - (k - 0.2) / 0.8;
          drawColumn(g, x, y, 130, 18, f.col, a);
          break;
        }
        case 'custom':
          if (!f.ground) f.draw(g, x, y, k, ox, oy);
          break;
        case 'ghost': {
          if (drawGhost(g, f, x, y, k)) break;
          // FX-PIXEL: a flat qi silhouette, solid then dithered away (no see-through copy of the figure).
          const pixel = fxPixelOn();
          const img = pixel || f.step ? ghostOf(f.img) : f.img;
          g.globalAlpha = f.step ? (k < 0.35 ? 1 : k < 0.7 ? 0.2 : 0.08) : pixel ? 1 - k : 0.32 * (1 - k);
          const rx = Math.round(x);
          const ry = Math.round(y);
          if (f.flip) {
            g.save();
            g.translate(rx, 0);
            g.scale(-1, 1);
            g.drawImage(img, artX(f.img) - f.ax, ry - f.ay + artY(f.img), artW(f.img), artH(f.img));
            g.restore();
          } else g.drawImage(img, rx - f.ax + artX(f.img), ry - f.ay + artY(f.img), artW(f.img), artH(f.img));
          g.globalAlpha = 1;
          break;
        }
      }
    }
    if (this.swordTrail.length > 1) {
      // Sword light: a ribbon fading and thinning toward its oldest end, a white core inside.
      g.lineCap = 'round';
      for (let k = 1; k < this.swordTrail.length; k++) {
        const a = this.swordTrail[k - 1]!;
        const b = this.swordTrail[k]!;
        if (Math.hypot(b.x - a.x, b.y - a.y) > 24) continue;
        const f = 1 - b.t / 0.5;
        g.strokeStyle = `rgba(120,215,255,${0.35 * f})`;
        g.lineWidth = 1 + 4 * f;
        g.beginPath();
        g.moveTo(a.x + ox, a.y + oy);
        g.lineTo(b.x + ox, b.y + oy);
        g.stroke();
        g.strokeStyle = `rgba(255,255,255,${0.8 * f})`;
        g.lineWidth = Math.max(1, 1.5 * f);
        g.beginPath();
        g.moveTo(a.x + ox, a.y + oy);
        g.lineTo(b.x + ox, b.y + oy);
        g.stroke();
      }
      g.lineCap = 'butt';
    }
    for (const s of this.swords) {
      g.save();
      g.translate(Math.round(s.x + ox), Math.round(s.y + oy));
      g.rotate(s.rot);
      if (s.needle) {
        g.fillStyle = 'rgba(232,106,168,.45)';
        g.fillRect(-8, -1, 12, 3);
        g.fillStyle = '#f4d0ff';
        g.fillRect(-6, 0, 10, 1);
        g.fillStyle = '#7a2a6a';
        g.fillRect(-7, 0, 2, 1);
        g.restore();
        continue;
      }
      g.fillStyle = 'rgba(120,220,255,.35)';
      g.fillRect(-11, -2, 17, 4);
      g.fillStyle = '#4fc4ff';
      g.fillRect(-9, -1, 3, 2);
      g.fillStyle = '#f2d27a';
      g.fillRect(-6, -2, 1, 4);
      g.fillStyle = '#f4fbff';
      g.fillRect(-5, -1, 9, 2);
      g.fillRect(4, 0, 2, 1);
      g.restore();
    }
    for (const q of this.parts) {
      if (q.t < 0) continue;
      const a = 1 - q.t / q.life;
      g.globalAlpha = a;
      if (q.streak) {
        g.strokeStyle = q.c;
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(Math.round(q.x + ox) + 0.5, Math.round(q.y + oy) + 0.5);
        g.lineTo(Math.round(q.x - q.vx * 0.035 + ox) + 0.5, Math.round(q.y - q.vy * 0.035 + oy) + 0.5);
        g.stroke();
      } else {
        g.fillStyle = q.c;
        const s = q.s ?? 1;
        g.fillRect(Math.round(q.x + ox), Math.round(q.y + oy), s, s);
      }
    }
    g.globalAlpha = 1;
  }
}

/**
 * Blade trail: a crescent that sweeps from `a0` to `a1`, thick at the leading edge and tapering
 * to nothing at the tail, with a bright cutting edge on the outside.
 */
function drawArc(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  f: Extract<Fx, { type: 'arc' }>,
  k: number,
): void {
  // The blade sweeps fast (head reaches the end at 25%) and the trail drains away behind it.
  const head = Math.min(1, k / 0.25);
  const tail = Math.max(0, (k - 0.15) / 0.85);
  if (head <= tail) return;
  const N = 24;
  const pt = (u: number, inset: number, out = 0): [number, number] => {
    const a = f.a0 + (f.a1 - f.a0) * u;
    return [
      x + Math.cos(a) * (f.rx - inset + out) * f.f,
      y + Math.sin(a) * (f.ry - (inset - out) * (f.ry / f.rx)),
    ];
  };
  const band = (thick: number, outset: number, fill: string) => {
    const outer: [number, number][] = [];
    const inner: [number, number][] = [];
    for (let i = 0; i <= N; i++) {
      const u = tail + ((head - tail) * i) / N;
      // Thickness swells toward the leading edge, like a smear behind a fast blade.
      const th = thick * Math.pow(i / N, 0.6);
      outer.push(pt(u, 0, outset));
      inner.push(pt(u, th, 0));
    }
    g.fillStyle = fill;
    g.beginPath();
    g.moveTo(...outer[0]!);
    for (const p of outer) g.lineTo(...p);
    for (let i = inner.length - 1; i >= 0; i--) g.lineTo(...inner[i]!);
    g.closePath();
    g.fill();
    return outer;
  };
  const a = k < 0.45 ? 1 : 1 - (k - 0.45) / 0.55;
  band(f.w * 1.7, 2, `rgba(${f.col},${0.18 * a})`); // soft outer glow
  band(f.w, 0, `rgba(${f.col},${0.55 * a})`); // coloured body
  const edge = band(f.w * 0.35, 0, `rgba(255,255,255,${0.85 * a})`); // white cutting core
  g.strokeStyle = `rgba(255,255,255,${a})`;
  g.lineWidth = 1;
  g.beginPath();
  const s0 = Math.floor(N * 0.5);
  g.moveTo(...edge[s0]!);
  for (let i = s0; i < edge.length; i++) g.lineTo(...edge[i]!);
  g.stroke();
  // A glint racing along the leading edge.
  if (k < 0.4) {
    const [gx, gy] = edge[edge.length - 1]!;
    g.fillStyle = `rgba(255,255,255,${1 - k * 2})`;
    g.fillRect(Math.round(gx) - 2, Math.round(gy), 5, 1);
    g.fillRect(Math.round(gx), Math.round(gy) - 2, 1, 5);
  }
}

/** A turning yin-yang disc lying on the ground (squashed for the isometric view). */
function drawYinYang(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  rot: number,
  alpha: number,
): void {
  if (alpha <= 0) return;
  g.save();
  g.globalAlpha = Math.min(1, alpha);
  g.translate(x, y - 1);
  g.scale(1, 0.5);
  g.rotate(rot);
  const light = '#f2f3f7';
  const dark = '#20222e';
  const circle = (cx: number, cy: number, rr: number, c: string) => {
    g.fillStyle = c;
    g.beginPath();
    g.arc(cx, cy, rr, 0, Math.PI * 2);
    g.fill();
  };
  circle(0, 0, r + 1.5, 'rgba(255,255,255,.6)');
  circle(0, 0, r, dark);
  g.fillStyle = light;
  g.beginPath();
  g.arc(0, 0, r, -Math.PI / 2, Math.PI / 2);
  g.fill();
  circle(0, -r / 2, r / 2, light);
  circle(0, r / 2, r / 2, dark);
  circle(0, -r / 2, r / 6, dark);
  circle(0, r / 2, r / 6, light);
  g.restore();
}
